import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PartnerAccountPermission, PartnerAccountPermissionSection } from 'vopay';

/**
 * `GET /partner/account/permissions` returns each row's display `Label` and each group's `Section`
 * slug as raw English, in every locale — so the permissions drawer rendered entirely in English for
 * French users. Per the i18n-conventions "config-/API-derived labels that double as logic keys" rule,
 * the raw values stay untouched everywhere they're matched on (`Permission` is the form field name,
 * `Section` is the grouping key) and only the *rendered* string is localized, here.
 *
 * The lookups are keyed on the `Permission` code and the `Section` slug, never on `Label`. Codes are
 * stable identifiers; the prose beside them is display copy that gets reworded. The equivalent billing
 * map had only prose to key on and shipped four rows in silent English from wording that was a
 * character off — and this endpoint alone sends the same `Label` ("Max Global Send Amount") for two
 * different permissions, which prose keys cannot even represent.
 *
 * Both tables are `Record`s over the union types, so `tsc` fails if a permission or section is added
 * to `vopay` without a label here. That only covers codes we know about: the unions are a snapshot of
 * a backend list, so a permission the API adds before the type is updated still arrives at runtime.
 * Both resolvers fall back for that case — an unmapped permission renders the API's own English
 * `Label` and an unmapped section its de-slugified name, i.e. exactly the pre-i18n behaviour, never
 * blank. Nothing flags that, so opening the drawer in French and reading it is what finds one.
 *
 * The resolvers are memoized on `t`, whose identity changes only when the language does, so the
 * drawer can list them in a `useMemo` dependency array: its field config is built once and rebuilt on
 * a language switch, rather than on every render.
 */
export default function usePermissionLabels() {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.Account.Permissions' });

  return useMemo(() => {
    const labels: Record<PartnerAccountPermission, string> = {
      // Transaction limits.
      MaxFundEFTAmount: t('Labels.maxFundEFTAmount', { defaultValue: 'Max Fund EFT Amount' }),
      MaxWithdrawEFTAmount: t('Labels.maxWithdrawEFTAmount', { defaultValue: 'Max Withdraw EFT Amount' }),
      MaxACHFundAmount: t('Labels.maxACHFundAmount', { defaultValue: 'Max ACH Fund Amount' }),
      MaxACHWithdrawAmount: t('Labels.maxACHWithdrawAmount', { defaultValue: 'Max ACH Withdraw Amount' }),
      SameDayMaxACHFundAmount: t('Labels.sameDayMaxACHFundAmount', { defaultValue: 'Max Same Day ACH Fund Amount' }),
      SameDayMaxACHWithdrawAmount: t('Labels.sameDayMaxACHWithdrawAmount', {
        defaultValue: 'Max Same Day ACH Withdraw Amount',
      }),
      MaxFedwireFundAmount: t('Labels.maxFedwireFundAmount', { defaultValue: 'Max Fedwire Fund Amount' }),
      MaxFedwireWithdrawAmount: t('Labels.maxFedwireWithdrawAmount', { defaultValue: 'Max Fedwire Withdraw Amount' }),
      MaxGCMAmount: t('Labels.maxGCMAmount', { defaultValue: 'Max Global Send Amount' }),
      MaxInteracMoneyAmount: t('Labels.maxInteracMoneyAmount', { defaultValue: 'Max Interac Money Amount' }),
      MaxInteracBulkPayoutAmount: t('Labels.maxInteracBulkPayoutAmount', {
        defaultValue: 'Max Interac e-Transfer® Send Amount',
      }),
      MaxPushToCardAmount: t('Labels.maxPushToCardAmount', { defaultValue: 'Max Debit Card - Push to Card Amount' }),
      MaxAFTAmount: t('Labels.maxAFTAmount', { defaultValue: 'Max Debit Card - Pull from Card Amount' }),
      MaxFundMyAccountAmount: t('Labels.maxFundMyAccountAmount', { defaultValue: 'Max Fund My Account Amount' }),
      MaxWithdrawMyAccountAmount: t('Labels.maxWithdrawMyAccountAmount', {
        defaultValue: 'Max Withdraw My Account Amount',
      }),
      MaxRTRWithdrawAmount: t('Labels.maxRTRWithdrawAmount', { defaultValue: 'Max RTR Withdraw Amount' }),
      MaxRTPWithdrawAmount: t('Labels.maxRTPWithdrawAmount', { defaultValue: 'Max RTP Withdraw Amount' }),
      MaxFednowWithdrawAmount: t('Labels.maxFednowWithdrawAmount', { defaultValue: 'Max Fednow Withdraw Amount' }),
      AutoBalanceTransferAmount: t('Labels.autoBalanceTransferAmount', {
        defaultValue: 'Auto Balance Transfer Amount',
      }),
      MaxCrossborderWithdrawAmount: t('Labels.maxCrossborderWithdrawAmount', {
        defaultValue: 'Max Global Send Amount',
      }),
      MaxBillPayAmount: t('Labels.maxBillPayAmount', { defaultValue: 'Max Bill Pay Amount' }),
      // Payment rails.
      GCMEnabled: t('Labels.gcmEnabled', { defaultValue: 'Global Send' }),
      EFTCollectEnabled: t('Labels.eftCollectEnabled', { defaultValue: 'EFT Collect' }),
      EFTSendEnabled: t('Labels.eftSendEnabled', { defaultValue: 'EFT Send' }),
      USDEFTCollectEnabled: t('Labels.usdEFTCollectEnabled', { defaultValue: 'USD EFT Collect' }),
      USDEFTSendEnabled: t('Labels.usdEFTSendEnabled', { defaultValue: 'USD EFT Send' }),
      RTRWithdrawEnabled: t('Labels.rtrWithdrawEnabled', { defaultValue: 'RTR Withdraw' }),
      PushToCardEnabled: t('Labels.pushToCardEnabled', { defaultValue: 'Debit Card - Push to Card' }),
      BillPayEnabled: t('Labels.billPayEnabled', { defaultValue: 'Bill Pay' }),
      AFTEnabled: t('Labels.aftEnabled', { defaultValue: 'Debit Card - Pull from Card' }),
      InteracMoneyRequestEnabled: t('Labels.interacMoneyRequestEnabled', {
        defaultValue: 'Interac e-Transfer® Request Money',
      }),
      TransactionApprovalWorkflowEnabled: t('Labels.transactionApprovalWorkflowEnabled', {
        defaultValue: 'Transaction Approval Workflow',
      }),
      IQ11Enabled: t('Labels.iq11Enabled', { defaultValue: 'IQ11' }),
      IQ11StandardEnabled: t('Labels.iq11StandardEnabled', { defaultValue: 'IQ11 Standard' }),
      IQ11BoostEnabled: t('Labels.iq11BoostEnabled', { defaultValue: 'IQ11 Boost' }),
      IQ11PlusEnabled: t('Labels.iq11PlusEnabled', { defaultValue: 'IQ11 Plus' }),
      IQ11AdvantageEnabled: t('Labels.iq11AdvantageEnabled', { defaultValue: 'IQ11 Advantage' }),
      InteracBulkPayoutEnabled: t('Labels.interacBulkPayoutEnabled', { defaultValue: 'Interac e-Transfer® Send' }),
      AutoBalanceTransferEnabled: t('Labels.autoBalanceTransferEnabled', { defaultValue: 'Auto Balance Transfer' }),
      PayLinkEnabled: t('Labels.payLinkEnabled', { defaultValue: 'eLinx™' }),
      EFTHoldRelease: t('Labels.eftHoldRelease', { defaultValue: 'EFT Hold Release' }),
      IQ11EFTHoldRelease: t('Labels.iq11EFTHoldRelease', { defaultValue: 'IQ11 EFT Hold Release' }),
      EFTHoldReleaseNonIQ11: t('Labels.eftHoldReleaseNonIQ11', { defaultValue: 'EFT Hold Release Non IQ11' }),
      HoldReleaseTime: t('Labels.holdReleaseTime', { defaultValue: 'Hold Release Time' }),
      FundMyAccountHoldRelease: t('Labels.fundMyAccountHoldRelease', { defaultValue: 'Fund My Account Hold Release' }),
      AccountTransferEnabled: t('Labels.accountTransferEnabled', { defaultValue: 'Account Transfer' }),
      FlagDuplicateTransactions: t('Labels.flagDuplicateTransactions', { defaultValue: 'Flag Duplicate Transactions' }),
      CreditCardEnabled: t('Labels.creditCardEnabled', { defaultValue: 'Credit Card' }),
      GooglePayEnabled: t('Labels.googlePayEnabled', { defaultValue: 'Google Pay' }),
      ApplePayEnabled: t('Labels.applePayEnabled', { defaultValue: 'Apple Pay' }),
      PaypalWithdrawEnabled: t('Labels.paypalWithdrawEnabled', { defaultValue: 'PayPal Withdraw' }),
      VenmoWithdrawEnabled: t('Labels.venmoWithdrawEnabled', { defaultValue: 'Venmo Withdraw' }),
      ACHFundEnabled: t('Labels.achFundEnabled', { defaultValue: 'ACH Fund' }),
      ACHWithdrawEnabled: t('Labels.achWithdrawEnabled', { defaultValue: 'ACH Withdraw' }),
      SameDayACHFundEnabled: t('Labels.sameDayACHFundEnabled', { defaultValue: 'Same Day ACH Fund' }),
      SameDayACHWithdrawEnabled: t('Labels.sameDayACHWithdrawEnabled', { defaultValue: 'Same Day ACH Withdraw' }),
      FedwireFundEnabled: t('Labels.fedwireFundEnabled', { defaultValue: 'Fedwire Fund' }),
      FedwireWithdrawEnabled: t('Labels.fedwireWithdrawEnabled', { defaultValue: 'Fedwire Withdraw' }),
      MoneyRequestGenerateUrlEnabled: t('Labels.moneyRequestGenerateUrlEnabled', {
        defaultValue: 'Interac e-Transfer® Request Money - Embedded',
      }),
      InboundEtransferEnabled: t('Labels.inboundEtransferEnabled', { defaultValue: 'Inbound Etransfer' }),
      RTPWithdrawEnabled: t('Labels.rtpWithdrawEnabled', { defaultValue: 'RTP Withdraw' }),
      FednowWithdrawEnabled: t('Labels.fednowWithdrawEnabled', { defaultValue: 'Fednow Withdraw' }),
      InteracHoldRelease: t('Labels.interacHoldRelease', { defaultValue: 'Interac Hold Release' }),
      CrossborderInclusive: t('Labels.crossborderInclusive', { defaultValue: 'Crossborder Inclusive' }),
      InteracBulkPayoutProcessingPriority: t('Labels.interacBulkPayoutProcessingPriority', {
        defaultValue: 'Interac e-Transfer® Send Processing Priority',
      }),
      InboundInteracHoldReleasePriority: t('Labels.inboundInteracHoldReleasePriority', {
        defaultValue: 'Inbound Interac Hold Release Priority',
      }),
      JournalEntryEnabled: t('Labels.journalEntryEnabled', { defaultValue: 'Journal Entry' }),
      FundMyAccountEnabled: t('Labels.fundMyAccountEnabled', { defaultValue: 'Fund My Account' }),
      WithdrawMyAccountEnabled: t('Labels.withdrawMyAccountEnabled', { defaultValue: 'Withdraw My Account' }),
      // Other permissions.
      TransactionLabelOverrideEnabled: t('Labels.transactionLabelOverrideEnabled', {
        defaultValue: 'Transaction Label Override',
      }),
      MicroDepositBankAccountVerificationEnabled: t('Labels.microDepositBankAccountVerificationEnabled', {
        defaultValue: 'VoPay Verify™ - Bank Verification (Micro Deposits)',
      }),
      InteracBasicVerificationEnabled: t('Labels.interacBasicVerificationEnabled', {
        defaultValue: 'VoPay Verify™ - Basic Interac® Verification (Email)',
      }),
      InteracEnhancedVerificationEnabled: t('Labels.interacEnhancedVerificationEnabled', {
        defaultValue: 'VoPay Verify™ - Enhanced Interac® Verification (Email)',
      }),
      CardBasicVerificationEnabled: t('Labels.cardBasicVerificationEnabled', {
        defaultValue: 'VoPay Verify™ - Basic Card Verification',
      }),
      CardEnhancedVerificationEnabled: t('Labels.cardEnhancedVerificationEnabled', {
        defaultValue: 'VoPay Verify™ - Enhanced Card Verification',
      }),
      EmailAddressBasicVerificationEnabled: t('Labels.emailAddressBasicVerificationEnabled', {
        defaultValue: 'VoPay Verify™ - Basic Email Verification',
      }),
      EmailAddressEnhancedVerificationEnabled: t('Labels.emailAddressEnhancedVerificationEnabled', {
        defaultValue: 'VoPay Verify™ - Enhanced Email Verification',
      }),
      PhoneNumberBasicVerificationEnabled: t('Labels.phoneNumberBasicVerificationEnabled', {
        defaultValue: 'VoPay Verify™ - Basic Phone Number Verification',
      }),
      PhoneNumberEnhancedVerificationEnabled: t('Labels.phoneNumberEnhancedVerificationEnabled', {
        defaultValue: 'VoPay Verify™ - Enhanced Phone Number Verification',
      }),
      DualProcessVerificationEnabled: t('Labels.dualProcessVerificationEnabled', {
        defaultValue: 'VoPay Verify™ - Non-doc ID Verification',
      }),
      AddressVerificationEnabled: t('Labels.addressVerificationEnabled', {
        defaultValue: 'VoPay Verify™ - Address Verification',
      }),
      IdBasicVerificationEnabled: t('Labels.idBasicVerificationEnabled', {
        defaultValue: 'VoPay Verify™ - Basic ID Verification',
      }),
      IdEnhancedVerificationEnabled: t('Labels.idEnhancedVerificationEnabled', {
        defaultValue: 'VoPay Verify™ - Enhanced ID Verification',
      }),
      NameScreenVerificationEnabled: t('Labels.nameScreenVerificationEnabled', {
        defaultValue: 'VoPay Verify™ - Name Screen Verification',
      }),
      ThirdPartyClientAccountTransactionsEnabled: t('Labels.thirdPartyClientAccountTransactionsEnabled', {
        defaultValue: 'Third Party Client Account Transactions',
      }),
      CPAFileGenerationEnabled: t('Labels.cpaFileGenerationEnabled', { defaultValue: 'CPA File Generation' }),
      AccountingSoftwareIntegrationEnabled: t('Labels.accountingSoftwareIntegrationEnabled', {
        defaultValue: 'VoPay 360 - Accounting Software Integration',
      }),
      BrandingEnabled: t('Labels.brandingEnabled', { defaultValue: 'Branding' }),
      BrandingEmailEnabled: t('Labels.brandingEmailEnabled', { defaultValue: 'Email Branding' }),
      BrandingELinxEnabled: t('Labels.brandingELinxEnabled', { defaultValue: 'eLinx™ Branding' }),
      BrandingPortalEnabled: t('Labels.brandingPortalEnabled', { defaultValue: 'Account Portal Branding' }),
      BrandingOnboardingEnabled: t('Labels.brandingOnboardingEnabled', { defaultValue: 'Onboarding Form Branding' }),
      LowBalanceAlertEnabled: t('Labels.lowBalanceAlertEnabled', { defaultValue: 'Low Balance Alert' }),
      InterestAccountEnabled: t('Labels.interestAccountEnabled', { defaultValue: 'Interest Account' }),
      ConvenienceFeeEnabled: t('Labels.convenienceFeeEnabled', { defaultValue: 'Convenience Fee' }),
      InterestMaturityType: t('Labels.interestMaturityType', { defaultValue: 'Interest Maturity Type' }),
      // Velocities.
      DailyLimitInboundBankAccount: t('Labels.dailyLimitInboundBankAccount', {
        defaultValue: 'Inbound Bank Account Daily Limit',
      }),
      DailyLimitAmountInboundBankAccount: t('Labels.dailyLimitAmountInboundBankAccount', {
        defaultValue: 'Inbound Bank Account Daily Limit Amount',
      }),
      WeeklyLimitInboundBankAccount: t('Labels.weeklyLimitInboundBankAccount', {
        defaultValue: 'Inbound Bank Account Weekly Limit',
      }),
      WeeklyLimitAmountInboundBankAccount: t('Labels.weeklyLimitAmountInboundBankAccount', {
        defaultValue: 'Inbound Bank Account Weekly Limit Amount',
      }),
      MonthlyLimitInboundBankAccount: t('Labels.monthlyLimitInboundBankAccount', {
        defaultValue: 'Inbound Bank Account Monthly Limit',
      }),
      MonthlyLimitAmountInboundBankAccount: t('Labels.monthlyLimitAmountInboundBankAccount', {
        defaultValue: 'Inbound Bank Account Monthly Limit Amount',
      }),
      DailyLimitInboundEmailAddress: t('Labels.dailyLimitInboundEmailAddress', {
        defaultValue: 'Inbound Email Address Daily Limit',
      }),
      DailyLimitAmountInboundEmailAddress: t('Labels.dailyLimitAmountInboundEmailAddress', {
        defaultValue: 'Inbound Email Address Daily Limit Amount',
      }),
      WeeklyLimitInboundEmailAddress: t('Labels.weeklyLimitInboundEmailAddress', {
        defaultValue: 'Inbound Email Address Weekly Limit',
      }),
      WeeklyLimitAmountInboundEmailAddress: t('Labels.weeklyLimitAmountInboundEmailAddress', {
        defaultValue: 'Inbound Email Address Weekly Limit Amount',
      }),
      MonthlyLimitInboundEmailAddress: t('Labels.monthlyLimitInboundEmailAddress', {
        defaultValue: 'Inbound Email Address Monthly Limit',
      }),
      MonthlyLimitAmountInboundEmailAddress: t('Labels.monthlyLimitAmountInboundEmailAddress', {
        defaultValue: 'Inbound Email Address Monthly Limit Amount',
      }),
      DailyLimitInboundDebitCard: t('Labels.dailyLimitInboundDebitCard', {
        defaultValue: 'Inbound Debit Card Daily Limit',
      }),
      DailyLimitAmountInboundDebitCard: t('Labels.dailyLimitAmountInboundDebitCard', {
        defaultValue: 'Inbound Debit Card Daily Limit Amount',
      }),
      WeeklyLimitInboundDebitCard: t('Labels.weeklyLimitInboundDebitCard', {
        defaultValue: 'Inbound Debit Card Weekly Limit',
      }),
      WeeklyLimitAmountInboundDebitCard: t('Labels.weeklyLimitAmountInboundDebitCard', {
        defaultValue: 'Inbound Debit Card Weekly Limit Amount',
      }),
      MonthlyLimitInboundDebitCard: t('Labels.monthlyLimitInboundDebitCard', {
        defaultValue: 'Inbound Debit Card Monthly Limit',
      }),
      MonthlyLimitAmountInboundDebitCard: t('Labels.monthlyLimitAmountInboundDebitCard', {
        defaultValue: 'Inbound Debit Card Monthly Limit Amount',
      }),
      DailyLimitInboundCreditCard: t('Labels.dailyLimitInboundCreditCard', {
        defaultValue: 'Inbound Credit Card Daily Limit',
      }),
      DailyLimitAmountInboundCreditCard: t('Labels.dailyLimitAmountInboundCreditCard', {
        defaultValue: 'Inbound Credit Card Daily Limit Amount',
      }),
      WeeklyLimitInboundCreditCard: t('Labels.weeklyLimitInboundCreditCard', {
        defaultValue: 'Inbound Credit Card Weekly Limit',
      }),
      WeeklyLimitAmountInboundCreditCard: t('Labels.weeklyLimitAmountInboundCreditCard', {
        defaultValue: 'Inbound Credit Card Weekly Limit Amount',
      }),
      MonthlyLimitInboundCreditCard: t('Labels.monthlyLimitInboundCreditCard', {
        defaultValue: 'Inbound Credit Card Monthly Limit',
      }),
      MonthlyLimitAmountInboundCreditCard: t('Labels.monthlyLimitAmountInboundCreditCard', {
        defaultValue: 'Inbound Credit Card Monthly Limit Amount',
      }),
      DailyLimitOutboundBankAccount: t('Labels.dailyLimitOutboundBankAccount', {
        defaultValue: 'Outbound Bank Account Daily Limit',
      }),
      DailyLimitAmountOutboundBankAccount: t('Labels.dailyLimitAmountOutboundBankAccount', {
        defaultValue: 'Outbound Bank Account Daily Limit Amount',
      }),
      WeeklyLimitOutboundBankAccount: t('Labels.weeklyLimitOutboundBankAccount', {
        defaultValue: 'Outbound Bank Account Weekly Limit',
      }),
      WeeklyLimitAmountOutboundBankAccount: t('Labels.weeklyLimitAmountOutboundBankAccount', {
        defaultValue: 'Outbound Bank Account Weekly Limit Amount',
      }),
      MonthlyLimitOutboundBankAccount: t('Labels.monthlyLimitOutboundBankAccount', {
        defaultValue: 'Outbound Bank Account Monthly Limit',
      }),
      MonthlyLimitAmountOutboundBankAccount: t('Labels.monthlyLimitAmountOutboundBankAccount', {
        defaultValue: 'Outbound Bank Account Monthly Limit Amount',
      }),
      DailyLimitOutboundEmailAddress: t('Labels.dailyLimitOutboundEmailAddress', {
        defaultValue: 'Outbound Email Address Daily Limit',
      }),
      DailyLimitAmountOutboundEmailAddress: t('Labels.dailyLimitAmountOutboundEmailAddress', {
        defaultValue: 'Outbound Email Address Daily Limit Amount',
      }),
      WeeklyLimitOutboundEmailAddress: t('Labels.weeklyLimitOutboundEmailAddress', {
        defaultValue: 'Outbound Email Address Weekly Limit',
      }),
      WeeklyLimitAmountOutboundEmailAddress: t('Labels.weeklyLimitAmountOutboundEmailAddress', {
        defaultValue: 'Outbound Email Address Weekly Limit Amount',
      }),
      MonthlyLimitOutboundEmailAddress: t('Labels.monthlyLimitOutboundEmailAddress', {
        defaultValue: 'Outbound Email Address Monthly Limit',
      }),
      MonthlyLimitAmountOutboundEmailAddress: t('Labels.monthlyLimitAmountOutboundEmailAddress', {
        defaultValue: 'Outbound Email Address Monthly Limit Amount',
      }),
      DailyLimitOutboundDebitCard: t('Labels.dailyLimitOutboundDebitCard', {
        defaultValue: 'Outbound Debit Card Daily Limit',
      }),
      DailyLimitAmountOutboundDebitCard: t('Labels.dailyLimitAmountOutboundDebitCard', {
        defaultValue: 'Outbound Debit Card Daily Limit Amount',
      }),
      WeeklyLimitOutboundDebitCard: t('Labels.weeklyLimitOutboundDebitCard', {
        defaultValue: 'Outbound Debit Card Weekly Limit',
      }),
      WeeklyLimitAmountOutboundDebitCard: t('Labels.weeklyLimitAmountOutboundDebitCard', {
        defaultValue: 'Outbound Debit Card Weekly Limit Amount',
      }),
      MonthlyLimitOutboundDebitCard: t('Labels.monthlyLimitOutboundDebitCard', {
        defaultValue: 'Outbound Debit Card Monthly Limit',
      }),
      MonthlyLimitAmountOutboundDebitCard: t('Labels.monthlyLimitAmountOutboundDebitCard', {
        defaultValue: 'Outbound Debit Card Monthly Limit Amount',
      }),
      DailyLimitInboundAmount: t('Labels.dailyLimitInboundAmount', { defaultValue: 'Inbound Daily Limit Amount' }),
      WeeklyLimitInboundAmount: t('Labels.weeklyLimitInboundAmount', { defaultValue: 'Inbound Weekly Limit Amount' }),
      MonthlyLimitInboundAmount: t('Labels.monthlyLimitInboundAmount', {
        defaultValue: 'Inbound Monthly Limit Amount',
      }),
      MonthlyInboundTransactionLimit: t('Labels.monthlyInboundTransactionLimit', {
        defaultValue: 'Inbound Monthly Transaction Limit',
      }),
      DailyLimitOutboundAmount: t('Labels.dailyLimitOutboundAmount', { defaultValue: 'Outbound Daily Limit Amount' }),
      WeeklyLimitOutboundAmount: t('Labels.weeklyLimitOutboundAmount', {
        defaultValue: 'Outbound Weekly Limit Amount',
      }),
      MonthlyLimitOutboundAmount: t('Labels.monthlyLimitOutboundAmount', {
        defaultValue: 'Outbound Monthly Limit Amount',
      }),
      MonthlyOutboundTransactionLimit: t('Labels.monthlyOutboundTransactionLimit', {
        defaultValue: 'Outbound Monthly Transaction Limit',
      }),
    };

    const sections: Record<PartnerAccountPermissionSection, string> = {
      'transaction-limits': t('Sections.transactionLimits', { defaultValue: 'Transaction Limits' }),
      'payment-rails': t('Sections.paymentRails', { defaultValue: 'Payment Rails' }),
      'other-permissions': t('Sections.otherPermissions', { defaultValue: 'Other Permissions' }),
      velocities: t('Sections.velocities', { defaultValue: 'Velocities' }),
    };

    // Both lookups go through string-keyed maps rather than indexing the records directly: the records
    // are exhaustive over the unions, so indexing them yields `string` and would make the fallbacks
    // below look like dead code, when they are the only thing standing between a new backend
    // permission and a blank label.
    const labelsByPermission = new Map<string, string>(Object.entries(labels));
    const sectionsBySlug = new Map<string, string>(Object.entries(sections));

    return {
      /** `apiLabel` is the row's raw `Label` — the fallback for a permission this map hasn't heard of. */
      resolvePermissionLabel: (permission: string, apiLabel: string): string =>
        labelsByPermission.get(permission) ?? apiLabel,

      resolveSectionLabel: (section: string): string => sectionsBySlug.get(section) ?? formatSectionSlug(section),
    };
  }, [t]);
}

/** "payment-rails" -> "Payment Rails". The drawer's pre-i18n rendering, kept as the section fallback. */
function formatSectionSlug(section: string): string {
  return section
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
